import React, { useEffect, useMemo, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import {
  TextBox,
  InputDatePicker,
  LoadingBox,
  PrivilegesChecker,
  RadioCheckBox,
  SelectBox,
  ToastBox,
  ConfirmModelDialogBox,
  GridTable,
  ButtonBox,
  GenericModelPopupPdfReportViewer,
  ErrorValidationBox,
} from "..";
import {
  formatToIsoString,
  generateGuid,
  getLabelName,
  getLookUpItemValue,
  getPagePrivileges,
  getUserId,
  handleError,
  isArabicCurrentLanguage,
  sumType,
} from "../../utils";
import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";
import {
  AccountAcBeginBalanceTypeEnum,
  ActionButtonsModel,
  ActionTypeEnum,
  AcTransactionDetailModel,
  AcTransactionSearchModel,
  AcTransactionTypeEnum,
  GridChangeTypeEnum,
  HasFormIdModel,
  InputDatePickerTimeDisplayModeEnum,
  LookupItemModel,
  PrintTypeEnum,
  RequestActionModel,
  RowStateEnum,
  SearchRequestPayloadModel,
  SelectItemModel,
  TextBoxVersionEnum,
  ToastModel,
  TransactionRowDataModel,
  ValidationErrorModel,
  VoucherTransactionFormModel,
} from "../../models";
import { useFormik } from "formik";
import setCurrentTransactionColumns from "./columns/currentTransactionColumns";
import {
  deleteAcTransaction,
  getAcTransactionFullDetailById,
  saveCurrentVoucher,
  searchAccountTransaction,
} from "../../serviceBroker/acTransactionsApiServiceBroker";
import { useJournalVoucher, useTransactions } from "../../hooks";
import { InputActionMeta } from "react-select";

const initialValues: VoucherTransactionFormModel = {
  id: "",
  code: "",
  date: new Date(),
  type: null,
  cmbxCostCenter: null,
  employee: null,
  currency: null,
  amount: 0,
  toAccount: null,
  valueType: AccountAcBeginBalanceTypeEnum.CreditAccount,
  note: "",
  remarks: "",
  rowState: RowStateEnum.Add,
};
const selectionOptionSchema = yup
  .object()
  .shape({ label: yup.string(), value: yup.string() });
const validationSchema = yup.object({
  code: yup.string(),
  date: yup.date().required(getLabelName("required")),
  //type: selectionOptionSchema. required(getLabelName("required")).nullable(),
  employee: selectionOptionSchema.required(getLabelName("required")).nullable(),
  cmbxCostCenter: selectionOptionSchema
    .required(getLabelName("required"))
    .nullable(),
  toAccount: selectionOptionSchema
    .required(getLabelName("required"))
    .nullable(),
  amount: yup
    .number()
    .moreThan(0, getLabelName("Amount must be greater than 0"))
    .required(getLabelName("required")),
  currency: selectionOptionSchema.required(getLabelName("required")).nullable(),
  valueType: yup.string().required(getLabelName("required")),
});

interface AcComplexTransactionProps extends HasFormIdModel {}

export const AcComplexTransaction: React.FC<AcComplexTransactionProps> = ({
  formId,
}) => {
  //#region variables
  const defaultPageSize: number = 50;
  const searchInitialValues: SearchRequestPayloadModel = {
    pageNumber: 1,
    pageSize: defaultPageSize,
  };
  //#endregion
  //#region states

  const {
    searchedTransactions,
    areTransactionsLoading,
    setPageNumber,
    setPageSize,
    currentPage,
    currentPageSize,
    setSearchParams: setData,

    totalRows,
    fetchAccountTransactions,
  } = useTransactions();

  const {
    loading,
    setLoading,
    accounts,
    storedCurrencies,
    costCenters,
    storedEmployeeList,
    userLookUps,
    refreshAccounts: getAccounts,
    searchAccounts,
  } = useJournalVoucher();

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: false,
    onSubmit: () => {},
  });
  const [searchParams, setSearchParams] =
    useState<SearchRequestPayloadModel>(searchInitialValues);

  const [currentJournalVoucher, setCurrentJournalVoucher] = useState<
    TransactionRowDataModel[]
  >([]);
  const [validationErrors, setValidationErrors] = useState<
    ValidationErrorModel[]
  >([]);
  const [showPrintoutPdfModel, setShowPrintoutPdfModel] = useState(false);
  const [printTransactionId, setPrintTransactionId] = useState(0);
  const [currentVoucherID, setCurrentVoucherID] = useState<number | null>(null);
  const [deletedVoucherID, setDeletedVoucherID] = useState<number | null>(null);
  const [isCurrentVoucherLoading, setIsCurrentVoucherLoading] =
    useState<boolean>(false);
  const [toastModel, setToastModel] = useState<ToastModel>({
    show: false,
    body: getLabelName("Operation Done Successfully"),
    variant: "success",
  });
  const [showDeleteModel, setShowDeleteModel] = useState(false);
  const isArabic = isArabicCurrentLanguage();
  const costCenter = useRef<any>();
  const toAccountRef = useRef<any>();
  const employee = useRef<any>();
  const currency = useRef<any>();
  const accountsSelectBoxMultiselectRef = useRef<any>();
  const usersSelectBoxMultiselectRef = useRef<any>();
  //#endregion
  //#region variables
  const totalCredit = sumType(
    AccountAcBeginBalanceTypeEnum.CreditAccount,
    currentJournalVoucher
  );
  const totalDebit = sumType(
    AccountAcBeginBalanceTypeEnum.DebitAccount,
    currentJournalVoucher
  );
  const isModifyingVoucher = currentVoucherID !== null;
  const isDataLoading =
    areTransactionsLoading || loading || isCurrentVoucherLoading;
  //#endregion
  //#region useEffects
  useEffect(() => {
    resetVoucherValues();
  }, [storedCurrencies, costCenters, storedEmployeeList]);
  useEffect(() => {
    if (totalCredit === totalDebit) setValidationErrors([]);
  }, [totalCredit, totalDebit]);
  useEffect(() => {
    if (currentVoucherID === null) return;
    setIsCurrentVoucherLoading(true);
    getAcTransactionFullDetailById(`${currentVoucherID}`).then(
      (voucherDetails) => {
        setIsCurrentVoucherLoading(false);
        setCurrentJournalVoucher(
          (
            voucherDetails?.Ac_TransactionDetails as AcTransactionDetailModel[]
          ).map(
            ({
              ID,
              Type: valueType,
              Account_ID,
              Value,
              CreationDate,
              AccountNameEn,
              AccountType,
              rowState,
              Remarks,
            }) => ({
              amount: Value,
              date: new Date(CreationDate),
              id: "" + ID,
              toAccount: {
                label: getLabelName(AccountNameEn),
                value: "" + Account_ID,
              },
              note: "",
              remarks: Remarks,
              valueType: valueType,
              type: { label: "", value: "" + AccountType },
              rowState,
              isSaved: true,
            })
          )
        );
        formik.setFieldValue(
          "date",
          new Date(voucherDetails?.CreationDate as string)
        );
        formik.setFieldValue(
          "cmbxCostCenter",
          costCenters.find(
            ({ value }) => value === "" + voucherDetails?.CostCenter_ID
          ) || null
        );
        formik.setFieldValue(
          "employee",
          storedEmployeeList.find(
            ({ value }) => value === "" + voucherDetails?.Employee_Id
          ) || null
        );
        formik.setFieldValue(
          "currency",
          storedCurrencies.find(
            ({ value }) => value === "" + voucherDetails?.Currency_ID
          ) || null
        );
        formik.setFieldValue("code", voucherDetails?.Code || "0");
        formik.setFieldValue("note", voucherDetails?.Note || "");
      }
    );
  }, [currentVoucherID]);
  useEffect(() => {
    const fillData = async () => {
      setLoading(true);

      setData({ ...searchParams });

      setLoading(false);
    };

    fillData().then(() => {});
  }, [
    searchParams.pageNumber,
    searchParams.pageSize,
    searchParams.searchGuid,
    searchParams.transactionNumber,
    searchParams.accountId,
  ]);
  //#endregion
  //#region functions
  const handleVoucherSelect = async (id: number) => {
    if (id === currentVoucherID) return;
    setCurrentVoucherID(id);
    formik.setErrors({});
  };
  const handleTransactionModification = (
    id: string,
    type: GridChangeTypeEnum,
    value?: number,
    note?: string
  ) => {
    setCurrentJournalVoucher((prev) =>
      prev.map((transaction) => {
        if (transaction.id === id)
          return {
            ...transaction,
            amount:
              type == GridChangeTypeEnum.Amount && value != null
                ? value
                : transaction.amount,
            note: type == GridChangeTypeEnum.Note ? note : transaction.note,
            remarks:
              type == GridChangeTypeEnum.Note ? note : transaction.remarks,
            rowState: transaction.isSaved
              ? RowStateEnum.Update
              : RowStateEnum.Add,
          };
        return transaction;
      })
    );
  };
  const resetVoucherValues = () => {
    if (storedCurrencies.length !== 0) {
      formik.setFieldValue("currency", {
        value: storedCurrencies[0].value,
        label: getLabelName(storedCurrencies[0].name),
      });
    }
    if (costCenters.length !== 0)
      formik.setFieldValue("cmbxCostCenter", {
        value: costCenters[0].value,
        label: getLabelName(costCenters[0].name),
      });
    if (storedEmployeeList.length !== 0)
      formik.setFieldValue("employee", {
        value: storedEmployeeList[0].value,
        label: getLabelName(storedEmployeeList[0].name),
      });
    if (accounts.length !== 0) {
      formik.setFieldValue("toAccount", {
        value: accounts[0].value,
        label: getLabelName(accounts[0].name),
      });
    }
  };
  const deleteActions: (deletedId: number) => ActionButtonsModel[] = (
    deletedID
  ) => {
    return [
      {
        text: getLabelName("yes"),
        onClick: async () => {
          await handleVoucherDeletion(deletedID);
          setShowDeleteModel(false);
          setToastModel((prev) => ({ ...prev, show: true }));
          setDeletedVoucherID(null);
        },
      },
      {
        text: getLabelName("No"),
        onClick: () => {
          setShowDeleteModel(false);
          setDeletedVoucherID(null);
        },
      },
    ];
  };
  const handleDeletion = (deletedItemID: string) => {
    setCurrentJournalVoucher((prev) =>
      prev
        .filter(({ id, isSaved }) => {
          return !(!isSaved && id === deletedItemID);
        })
        .map((transaction) => {
          if (deletedItemID === transaction.id)
            return { ...transaction, rowState: RowStateEnum.Delete };
          return transaction;
        })
    );
  };
  const handleVoucherDeletion = async (id: number) => {
    try {
      setIsCurrentVoucherLoading(true);
      await deleteAcTransaction(id, getUserId());
      await fetchAccountTransactions();
      if (id === currentVoucherID) resetFormData();
      setIsCurrentVoucherLoading(false);
    } catch (err) {
      alert(handleError("item cannot be deleted"));
    }
  };
  const handleVoucherPrint = (id: number) => {
    if (id === 0) return;
    setPrintTransactionId(id);
    setShowPrintoutPdfModel(true);
  };
  //#endregion
  //#region helpers
  const resetFormData = () => {
    setCurrentJournalVoucher([]);
    setCurrentVoucherID(null);
    formik.resetForm();
    toAccountRef.current.clearValue();
    resetVoucherValues();
  };
  const isRemainingTransactionAvailable = (
    transactions: TransactionRowDataModel[]
  ) => {
    return transactions.some(
      ({ rowState }) => rowState !== RowStateEnum.Delete
    );
  };
  const onActionEvent = async (request: RequestActionModel) => {
    switch (request.action) {
      case ActionTypeEnum.Update:
        await handleVoucherSelect(request.id || request.request.ID);
        break;
      case ActionTypeEnum.Delete:
        setShowDeleteModel(true);
        setDeletedVoucherID(request.id || request.request.ID);
        break;
      case ActionTypeEnum.Print:
        handleVoucherPrint(request.id || request.request.ID);
        break;
    }
  };
  //#endregion
  //#region component
  const subHeaderComponent = useMemo(() => {
    return (
      <>
        <div className="row row-cols-1 row-cols-xxl-3 row-cols-xl-3 row-cols-lg-3 row-cols-md-2 row-cols-sm-1 g-md-4 g-sm-4">
          <SelectBox
            labelName={getLabelName("Account")}
            isSingleSelect={true}
            selectedValues={
              searchParams.accountId
                ? [searchParams.accountId.toString()]
                : [""]
            }
            source={accounts}
            onStatusChange={(e: LookupItemModel) => {
              const val = getLookUpItemValue(e);
              setSearchParams({
                ...searchParams,
                accountId: val == null ? undefined : val,
              });
            }}
            multiselectRef={accountsSelectBoxMultiselectRef}
          />
          <SelectBox
            labelName={getLabelName("User")}
            isSingleSelect={true}
            selectedValues={
              searchParams.userId ? [searchParams.userId.toString()] : [""]
            }
            source={userLookUps}
            onStatusChange={(e: LookupItemModel) => {
              const val = getLookUpItemValue(e);
              setSearchParams({
                ...searchParams,
                userId: val == null ? undefined : val,
              });
            }}
            multiselectRef={usersSelectBoxMultiselectRef}
          />
          <TextBox
            labelName={getLabelName("code")}
            inputName={"transactionNumber"}
            inputValue={searchParams.transactionNumber}
            onChange={(e: any) => {
              setSearchParams({
                ...searchParams,
                transactionNumber: e.target.value,
              });
            }}
          />
          <InputDatePicker
            selectedDate={
              searchParams.fromDate != null ? searchParams.fromDate : undefined
            }
            isClearable
            InputLabel={"from Date"}
            selectsEnd
            timeMode={InputDatePickerTimeDisplayModeEnum.TimeSelect}
            startDate={searchParams.fromDate}
            className="form-control"
            // minDate={searchParams.fromDate}
            endDate={searchParams.toDate}
            onChange={(date) => {
              setSearchParams({
                ...searchParams,
                fromDate: date === null ? undefined : date,
              });
            }}
          />
          <InputDatePicker
            selectedDate={
              searchParams.toDate != null ? searchParams.toDate : undefined
            }
            isClearable
            InputLabel={"to Date"}
            timeMode={InputDatePickerTimeDisplayModeEnum.TimeSelect}
            selectsEnd
            startDate={searchParams.fromDate}
            className="form-control"
            minDate={searchParams.fromDate}
            endDate={searchParams.toDate}
            onChange={(date) => {
              setSearchParams({
                ...searchParams,
                toDate: date === null ? undefined : date,
              });
            }}
          />
          <div className="col d-flex align-items-end justify-content-end">
            <PrivilegesChecker formId={formId} action="EnableSearch">
              <ButtonBox
                iconType="account-search"
                className="btn-sm mx-2 btn-gradient-primary mb-3"
                onClick={() => {
                  setSearchParams({
                    ...searchParams,
                    searchGuid: generateGuid(),
                  });
                  //onActionEvent({request: null, action: ActionTypeEnum.Search})
                }}
              >
                {getLabelName("Search")}
              </ButtonBox>
            </PrivilegesChecker>
            <ButtonBox
              iconType="receipt"
              variant="danger"
              className="btn btn-sm mb-3"
              onClick={() => {
                // Clear the select boxes using their refs
                accountsSelectBoxMultiselectRef.current.clearValue();
                usersSelectBoxMultiselectRef.current.clearValue();

                // Reset the search params
                const resetParams = {
                  ...searchInitialValues,
                  userId: undefined,
                  accountId: undefined,
                  fromDate: undefined,
                  toDate: undefined,
                };

                // Update the search params state
                setSearchParams({
                  ...searchInitialValues,
                });

                // Also update the data to trigger a search with empty parameters
                setData(resetParams);
              }}
            >
              {getLabelName("Cancel")}
            </ButtonBox>
          </div>
        </div>
      </>
    );
  }, [searchParams, accounts, userLookUps]);
  //#endregion
  //#region html
  return (
    <>
      {toastModel.show && (
        <ToastBox
          isShown={toastModel.show}
          header={toastModel.header}
          body={toastModel.body}
          variant={toastModel.variant}
          delayDuration={toastModel.delayDuration}
          onCloseEvent={() => {
            setToastModel({ ...toastModel, show: false });
          }}
        />
      )}
      <ConfirmModelDialogBox
        isModelVisible={showDeleteModel}
        onCloseEvent={() => {
          setShowDeleteModel(false);
          setDeletedVoucherID(null);
        }}
        actions={deleteActions(deletedVoucherID!)}
      >
        <>{getLabelName("Are You Sure You Want Delete ")}</>
      </ConfirmModelDialogBox>
      {showPrintoutPdfModel && (
        <GenericModelPopupPdfReportViewer
          keys={[{ key: "transactionId", value: printTransactionId }]}
          type={PrintTypeEnum.JournalVoucher}
          onCloseEvent={() => {
            setShowPrintoutPdfModel(false);
          }}
        />
      )}

      {isDataLoading && <LoadingBox />}
      {<ErrorValidationBox errors={validationErrors} />}
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            {getLabelName("Complex Jurnal voutcher")}
          </Accordion.Header>
          <Accordion.Body>
            <form
              onSubmit={async (e: React.FormEvent) => {
                e.preventDefault();
                const errors = await formik.validateForm();
                formik.setErrors(errors);
                if (Object.keys(errors).length !== 0) return;
                setCurrentJournalVoucher((prevState) => [
                  ...prevState,
                  {
                    amount: formik.values.amount,
                    note: formik.values.note,
                    remarks: formik.values.note,
                    toAccount: formik.values.toAccount,
                    valueType: +formik.values.valueType,
                    date: formik.values.date,
                    type: formik.values.type,
                    id: uuidv4(),
                    rowState: RowStateEnum.Add,
                    isSaved: false,
                  },
                ]);
                //resetTransactionData();
              }}
            >
              <div className="row row-cols-1 row-cols-xxl-3 row-cols-xl-3 row-cols-lg-3 row-cols-md-2 row-cols-sm-1 g-md-4 g-sm-4 mb-3">
                <TextBox
                  inputValue={formik.values.code}
                  inputName="code"
                  type={"text"}
                  onChange={formik.handleChange}
                  labelName={getLabelName("code")}
                />
                <InputDatePicker
                  id="datePicker"
                  timeMode={InputDatePickerTimeDisplayModeEnum.None}
                  selectedDate={new Date(formik.values.date.toString())}
                  className="form-control"
                  InputLabel="date"
                  name="date"
                  onChange={(date: Date) => {
                    formik.values.date = date;
                    formik.setFieldValue("date", date);
                  }}
                  maxDate={new Date()}
                />
                <SelectBox
                  labelName={getLabelName("cmbxCostCenter")}
                  inputName="cmbxCostCenter"
                  multiselectRef={costCenter}
                  selectedValues={
                    formik.values.cmbxCostCenter === null
                      ? null
                      : [formik.values.cmbxCostCenter.value]
                  }
                  source={costCenters}
                  onStatusChange={(selectedValue: SelectItemModel) =>
                    formik.setFieldValue("cmbxCostCenter", selectedValue)
                  }
                  isSingleSelect={true}
                  errorText={formik.errors.cmbxCostCenter}
                />
              </div>
              <div className="row row-cols-1 row-cols-xxl-3 row-cols-xl-3 row-cols-lg-3 row-cols-md-2 row-cols-sm-1 g-md-4 g-sm-4 mb-3">
                <SelectBox
                  labelName={getLabelName("employee")}
                  inputName="employee"
                  multiselectRef={employee}
                  selectedValues={
                    formik.values.employee === null
                      ? null
                      : [formik.values.employee.value]
                  }
                  source={storedEmployeeList}
                  onStatusChange={(selectedValue: SelectItemModel) =>
                    formik.setFieldValue("employee", selectedValue)
                  }
                  isSingleSelect={true}
                  errorText={formik.errors.employee}
                />
                <SelectBox
                  labelName={getLabelName("currency")}
                  inputName="currency"
                  multiselectRef={currency}
                  selectedValues={
                    formik.values.currency === null
                      ? null
                      : [formik.values.currency.value]
                  }
                  source={storedCurrencies}
                  onStatusChange={(selectedValue: SelectItemModel) =>
                    formik.setFieldValue("currency", selectedValue)
                  }
                  isSingleSelect={true}
                  errorText={formik.errors.currency}
                />
              </div>
              <div className="row row-cols-12 mb-3">
                <TextBox
                  type={"textarea"}
                  labelName={"Note"}
                  inputName="note"
                  inputValue={formik.values.note}
                  onChange={formik.handleChange}
                  version={TextBoxVersionEnum.V2}
                  // optimizeTyping={true}
                  //resetKey={generateGuid()}
                />
              </div>
              <div className="row row-cols-1 row-cols-xxl-3 row-cols-xl-3 row-cols-lg-3 row-cols-md-2 row-cols-sm-1 g-md-4 g-sm-4 mb-3">
                <SelectBox
                  labelName={getLabelName("toAccount")}
                  inputName="toAccount"
                  multiselectRef={toAccountRef}
                  selectedValues={
                    formik.values.toAccount === null
                      ? null
                      : ["" + formik.values.toAccount.value]
                  }
                  onInputChange={(
                    inputValue: string,
                    { action }: InputActionMeta
                  ) => {
                    if (action === "input-change") {
                      // Simply call the debounced search function
                      searchAccounts(inputValue);
                    }
                    return inputValue;
                  }}
                  source={accounts}
                  onStatusChange={(selectedValue: SelectItemModel) => {
                    formik.setFieldValue("toAccount", selectedValue);
                  }}
                  isSingleSelect={true}
                  errorText={formik.errors.toAccount}
                  isDataLoading={loading}
                />
                <TextBox
                  labelName={getLabelName("Money")}
                  type={"number"}
                  inputName="amount"
                  inputValue={formik.values.amount}
                  onChange={formik.handleChange}
                  errorText={formik.errors.amount}
                />
                <RadioCheckBox
                  items={[
                    {
                      text: getLabelName("Credit"),
                      value: "" + AccountAcBeginBalanceTypeEnum.CreditAccount,
                      name: "valueType",
                    },
                    {
                      text: getLabelName("Debit"),
                      value: "" + AccountAcBeginBalanceTypeEnum.DebitAccount,
                      name: "valueType",
                    },
                  ]}
                  onChange={formik.handleChange}
                  type={"radio"}
                  selectedValues={["" + formik.values.valueType]}
                />
              </div>
              <div className="row mt-3 ">
                <div className="search-form-item d-flex align-items-end justify-content-end">
                  <ButtonBox
                    type="submit"
                    iconType="plus"
                    id="addItemBtn"
                    variant=""
                    className="btn-gradient-info me-2"
                    size="sm"
                  >
                    {getLabelName("add")}
                  </ButtonBox>
                </div>
              </div>
              <div className="row row-cols-1 mt-5">
                <GridTable
                  columns={setCurrentTransactionColumns({
                    handleDeletion: handleDeletion,
                    handleTransactionModification:
                      handleTransactionModification,
                  })}
                  data={currentJournalVoucher.filter(
                    ({ rowState }) => rowState !== RowStateEnum.Delete
                  )}
                  totalRows={totalRows}
                  currentPage={currentPage}
                  pageSize={currentPageSize}
                  onCurrentPageChange={() => {}}
                  onPageSizeChange={() => {}}
                  paginationType="none"
                />
                {isRemainingTransactionAvailable(currentJournalVoucher) && (
                  <div
                    className={`row row-cols-1 row-cols-xxl-2 row-cols-xl-2 row-cols-lg-2 row-cols-md-2 row-cols-sm-1 g-md-4 g-sm-4 mt-1 ${
                      totalCredit !== totalDebit ? "text-danger" : ""
                    }`}
                    style={{ marginRight: 0 }}
                  >
                    <div>
                      <span>
                        {getLabelName("total credit")}: {totalCredit}
                      </span>
                    </div>
                    <div>
                      <span>
                        {getLabelName("total debit")}: {totalDebit}
                      </span>
                    </div>
                  </div>
                )}
                {isRemainingTransactionAvailable(currentJournalVoucher) && (
                  <PrivilegesChecker
                    formId={formId}
                    action={currentVoucherID ? "EnableUpdate" : "EnableSave"}
                  >
                    <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 d-flex justify-content-end ">
                      <ButtonBox
                        iconType="content-save"
                        type="button"
                        onClick={async () => {
                          if (totalDebit !== totalCredit)
                            return setValidationErrors((prev) => [
                              ...prev,
                              {
                                MessageAr:
                                  "إجمالي مبلغ الدائن لا يساوي إجمالي المدين",
                                MessageEn: "Total Credit Not Equal Total Debit",
                              },
                            ]);

                          const rowState = isModifyingVoucher
                            ? RowStateEnum.Update
                            : RowStateEnum.Add;
                          const voucher = {
                            Date: formatToIsoString(formik.values.date),
                            Code: formik.values.code,
                            CostCenter_ID: formik.values.cmbxCostCenter
                              ?.value as string,
                            Currency_ID: formik.values.currency
                              ?.value as string,
                            User_ID: getUserId(),
                            Employee_Id: formik.values.employee
                              ?.value as string,
                            Note: formik.values.note ?? "",
                            Ac_TransactionDetails: currentJournalVoucher.map(
                              ({
                                amount,
                                date,
                                toAccount,
                                valueType,
                                note,
                                remarks,
                                id,
                                rowState,
                              }) => {
                                return {
                                  Note: note,
                                  Remarks: remarks,
                                  Type: valueType,
                                  Value: +amount,
                                  Account_ID: toAccount?.value,
                                  // Temporary createdBy ID
                                  CreatedBy: getUserId(),
                                  rowState,
                                  ID: id,
                                  CreationDate: formatToIsoString(date),
                                  TransactionHeaders_ID:
                                    isModifyingVoucher &&
                                    currentVoucherID !== null
                                      ? currentVoucherID
                                      : undefined,
                                };
                              }
                            ),
                            CreatedBy: getUserId(),
                            rowState,
                            ID:
                              isModifyingVoucher && currentVoucherID !== null
                                ? currentVoucherID
                                : undefined,
                          };
                          setIsCurrentVoucherLoading(true);
                          await saveCurrentVoucher(voucher);
                          await fetchAccountTransactions();
                          setIsCurrentVoucherLoading(false);
                          setToastModel((prev) => ({ ...prev, show: true }));
                          resetFormData();
                        }}
                        className="btn-gradient-primary mx-3 btn-fw"
                      >
                        {getLabelName(currentVoucherID ? "Update" : "Save")}
                      </ButtonBox>
                      <ButtonBox
                        variant="danger"
                        type="button"
                        iconType="receipt"
                        role="span"
                        className="btn btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          resetFormData();
                          setSearchParams({ ...searchInitialValues });
                        }}
                      >
                        {getLabelName("New")}
                      </ButtonBox>
                    </div>
                  </PrivilegesChecker>
                )}
              </div>
            </form>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <Accordion defaultActiveKey="1" className="mt-5">
        <Accordion.Item eventKey="1">
          <Accordion.Header>{getLabelName("Previous Data")}</Accordion.Header>
          <Accordion.Body>
            {/* <PrivilegesChecker FormId={FormId} action="EnableSearch">
              <div className="mb-4">
                <AcComplexTransactionSearch onActionEvent={handleAction} />
              </div>
            </PrivilegesChecker> */}
            {getPagePrivileges().EnableSearch && (
              <PrivilegesChecker formId={formId} action="EnableSearch">
                {subHeaderComponent}
              </PrivilegesChecker>
            )}

            <div className="row row-cols-1">
              <GridTable
                columnsProps={{
                  actions: [
                    ActionTypeEnum.Update,
                    ActionTypeEnum.Delete,
                    ActionTypeEnum.Print,
                  ],

                  isArabic,
                  onActionEvent,
                }}
                formId={formId}
                data={searchedTransactions}
                externalLoading={loading}
                totalRows={totalRows}
                currentPage={currentPage}
                pageSize={currentPageSize}
                onCurrentPageChange={setPageNumber}
                onPageSizeChange={setPageSize}
                paginationType="server"
              />
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </>
  );
  //#endregion
};
